import * as XLSX from 'xlsx-js-style';

const dashboardAccentBlue = 'FF5E8EF0';
const sectionBorder = { style: 'thin', color: { rgb: dashboardAccentBlue } };

export type BreakdownExportSummary = {
  ordersCount: number;
  sales: number;
  totalWithTips: number;
  tips: number;
  dinersCount: number;
  avgPerDiner: number;
  food: number;
  beverages: number;
};

export type BreakdownExportOrderRow = {
  status: string;
  orderCode: string;
  closingTime: string;
  table: string;
  openingTime: string;
  waiter: string;
  sales: number;
  totalWithTips: number;
  tips: number;
  dinersCount: number | string;
  average: number | string;
  canceledItemsCount: number | string;
  discounts: number | string;
};

const toSafeFileBaseName = (value: string): string => value
  .trim()
  .replace(/[\\/:*?"<>|]/g, '-')
  .replace(/\s+/g, ' ')
  .replace(/-+/g, '-')
  .replace(/^[.\-\s]+|[.\-\s]+$/g, '') || 'פירוט הזמנות';

const toSafeSheetName = (value: string): string => {
  const normalized = value
    .trim()
    .replace(/[\\/:*?\[\]]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/-+/g, '-')
    .replace(/^[.\-\s]+|[.\-\s]+$/g, '');

  return (normalized || 'פירוט הזמנות').slice(0, 31);
};

export const downloadBreakdownOrdersExcel = (
  title: string,
  summary: BreakdownExportSummary,
  rows: BreakdownExportOrderRow[],
): void => {
  const data: Array<Array<string | number>> = [
    [title],
    [],
    ['סיכום'],
    ['הזמנות', 'מכירות', 'סהכ עם טיפ', 'טיפ', 'סועדים', 'ממוצע לסועד', 'אוכל', 'משקאות'],
    [summary.ordersCount, summary.sales, summary.totalWithTips, summary.tips, summary.dinersCount, summary.avgPerDiner, summary.food, summary.beverages],
    [],
    ['הזמנות'],
    ['סטטוס', 'מזהה הזמנה', 'שולחן', 'שעת פתיחה', 'שעת סגירה', 'מלצר', 'מכירות', 'סהכ עם טיפ', 'טיפ', 'סועדים', 'ממוצע', 'פריטים מבוטלים', 'הנחה'],
    ...rows.map((row) => [
      row.status,
      row.orderCode,
      row.table,
      row.openingTime,
      row.closingTime,
      row.waiter,
      row.sales,
      row.totalWithTips,
      row.tips,
      row.dinersCount,
      row.average,
      row.canceledItemsCount,
      row.discounts,
    ]),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(data);
  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 12 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 7 } },
    { s: { r: 6, c: 0 }, e: { r: 6, c: 12 } },
  ];
  worksheet['!cols'] = [
    { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 18 },
    { wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 16 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, toSafeSheetName(title));
  workbook.Workbook = workbook.Workbook ?? {};
  workbook.Workbook.Views = [{ RTL: true }];

  const toColumnName = (index: number): string => String.fromCharCode(65 + index);
  const setRowStyle = (rowNumber: number, columnCount: number, bold = false, blue = false) => {
    for (let column = 0; column < columnCount; column += 1) {
      const cell = worksheet[`${toColumnName(column)}${rowNumber}`];
      if (!cell) {
        continue;
      }
      const currentStyle = (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s ?? {};
      const currentFont = (currentStyle.font as Record<string, unknown> | undefined) ?? {};
      const currentAlignment = (currentStyle.alignment as Record<string, unknown> | undefined) ?? {};
      (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s = {
        ...currentStyle,
        font: bold || blue
          ? { ...currentFont, ...(bold ? { bold: true } : {}), ...(blue ? { color: { rgb: dashboardAccentBlue } } : {}) }
          : currentFont,
        alignment: { ...currentAlignment, horizontal: 'center', vertical: 'center', wrapText: true },
      };
    }
  };
  const setSectionOutline = (firstRow: number, lastRow: number, columnCount: number, includeTop = true) => {
    for (let row = firstRow; row <= lastRow; row += 1) {
      for (let column = 0; column < columnCount; column += 1) {
        const cell = worksheet[`${toColumnName(column)}${row}`];
        if (!cell) {
          continue;
        }
        const currentStyle = (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s ?? {};
        const currentBorder = (currentStyle.border as Record<string, unknown> | undefined) ?? {};
        (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s = {
          ...currentStyle,
          border: {
            ...currentBorder,
            ...(includeTop && row === firstRow ? { top: sectionBorder } : {}),
            ...(row === lastRow ? { bottom: sectionBorder } : {}),
            ...(column === 0 ? { left: sectionBorder } : {}),
            ...(column === columnCount - 1 ? { right: sectionBorder } : {}),
          },
        };
      }
    }
  };
  const setMergedTitleOutline = (rowNumber: number) => {
    const cell = worksheet[`A${rowNumber}`];
    if (!cell) {
      return;
    }
    const currentStyle = (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s ?? {};
    const currentBorder = (currentStyle.border as Record<string, unknown> | undefined) ?? {};
    (cell as XLSX.CellObject & { s?: Record<string, unknown> }).s = {
      ...currentStyle,
      border: { ...currentBorder, top: sectionBorder, left: sectionBorder, right: sectionBorder },
    };
  };

  data.forEach((row, index) => setRowStyle(index + 1, row.length));
  setRowStyle(1, 13, true, true);
  setRowStyle(3, 8, true, true);
  setRowStyle(4, 8, true);
  setRowStyle(7, 13, true, true);
  setRowStyle(8, 13, true);
  setMergedTitleOutline(3);
  setMergedTitleOutline(7);
  setSectionOutline(4, 5, 8, false);
  setSectionOutline(8, rows.length + 8, 13, false);
  XLSX.writeFile(workbook, `${toSafeFileBaseName(title)}.xlsx`, { cellStyles: true });
};